import { useQuery } from '@apollo/client'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../apollo-graphql/index'
import {
  GET_PROFILE_BY_UID,
  GET_PUBLIC_PROFILE,
} from '../../query'
import type {
  GetProfileByUidResponse,
  GetPublicProfileResponse,
  UserProfile,
} from '../../types'

interface UseProfileByUidParams {
  /** Profile uid to fetch. When falsy, the query is skipped. */
  uid: string | null | undefined
  /** Active app language (e.g. 'en', 'es-LA'). Threaded from parent per
   *  Community CLAUDE.md rule #4 — prevents Apollo cache key drift. */
  language: string
  /** JWT token. When present, GET_PROFILE_BY_UID runs (auth-only fields
   *  resolve). When absent/null, the public query runs. Anonymous viewers
   *  MUST hit GET_PUBLIC_PROFILE — the auth-gated query null-propagates the
   *  whole payload otherwise. */
  jwt?: string | null
}

interface UseProfileByUidResult {
  profile: UserProfile | null
  loading: boolean
  error: unknown
}

type ProfileQueryData = GetProfileByUidResponse | GetPublicProfileResponse

/**
 * Fetch a single user profile by uid for "card-density" surfaces (AI search
 * results, ConnectionsModal rows). Returns the same `UserProfile` shape as
 * the profile-page queries but always opts out of activity, watch-history,
 * and member-only fields — those payloads are heavy and the consumer cards
 * never render them.
 *
 * Selects `GET_PROFILE_BY_UID` when a JWT is supplied and `GET_PUBLIC_PROFILE`
 * for anonymous viewers. The viewer's `visibilityLevel` comes back inside
 * `viewerContext`, so per-field gating in the card uses that, not the JWT.
 *
 * Fetch policy is `cache-and-network`: the same `UserProfile:<uid>` entity
 * may already be in cache from a previous /profile/:uid visit, so we paint
 * cached values immediately and revalidate in the background.
 */
export default function useProfileByUid ({
  uid,
  language,
  jwt,
}: UseProfileByUidParams): UseProfileByUidResult {
  const query = jwt ? GET_PROFILE_BY_UID : GET_PUBLIC_PROFILE
  const { data, loading, error } = useQuery<ProfileQueryData>(query, {
    variables: {
      uid,
      language,
      contentLanguage: [language],
      // The fragment requires these as non-null Booleans. The card omits
      // activity/history surfaces entirely, so all three are forced to false.
      // Apollo cache normalization is safe: fields gated off here won't
      // overwrite fields populated by the profile page running with `true`.
      includeMemberFields: false,
      includeProfileActivity: false,
      includeWatchHistory: false,
    },
    skip: !uid,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: jwt ? { jwt } : undefined,
  })

  const byUid = (data as GetProfileByUidResponse | undefined)?.userProfileByUid
  const publicProfile = (data as GetPublicProfileResponse | undefined)?.userProfilePublic
  const profile: UserProfile | null = byUid ?? publicProfile ?? null

  return { profile, loading, error }
}
